import React, { lazy, Suspense } from 'react';
import StepWrapper from '../components/stepWrapper';
import { isDiscountScreenVariantB } from '../utils/scripts/discountFortuneAbTest';

import Mail from '../steps/mail/mail';

// ─── hydratable lazy (pre-rendered first screens) ────────────────────────────
// Same contract as React.lazy (throws the loader promise → nearest Suspense),
// but the chunk can be resolved AHEAD of render via preloadStepComponent(), after
// which the component renders synchronously on the very first attempt. Required
// for hydrateRoot: a component that suspends during hydration makes React drop
// the pre-rendered DOM (client-rendered Suspense fallback) instead of adopting it.
type LoadedStepModule = { default: React.ComponentType<Record<string, unknown>> };
const hydratableLoaders: Record<string, () => Promise<unknown>> = {};

function hydratableLazy(
  stepName: string,
  loader: () => Promise<LoadedStepModule>,
): React.ComponentType<Record<string, unknown>> {
  let Resolved: React.ComponentType<Record<string, unknown>> | null = null;
  let pending: Promise<unknown> | null = null;
  const load = (): Promise<unknown> => {
    if (!pending) {
      pending = loader().then((m) => {
        Resolved = m.default;
      });
    }
    return pending;
  };
  hydratableLoaders[stepName] = load;
  function HydratableStep(props: Record<string, unknown>): React.ReactElement {
    // Suspense contract: throwing the pending thenable is how a component
    // suspends (React.lazy does exactly this internally).
    // eslint-disable-next-line @typescript-eslint/no-throw-literal
    if (!Resolved) throw load();
    // eslint-disable-next-line react/jsx-props-no-spreading
    return <Resolved {...props} />;
  }
  return HydratableStep;
}

// Resolve a step component's chunk before hydration (resolves immediately for
// steps that were not registered as hydratable).
export function preloadStepComponent(stepName: string): Promise<unknown> {
  const load = hydratableLoaders[stepName];
  return load ? load() : Promise.resolve();
}

const CustomV2StartPageV4 = hydratableLazy('customV2_startPageV4', () => import('../steps/start/startPageV4'));
const CustomV2StartPageV29 = hydratableLazy('customV2_startPageV29', () => import('../steps/start/startPageV29'));
const CustomV2StartPageV30 = hydratableLazy('customV2_startPageV30', () => import('../steps/start/startPageV30'));
const CustomV2StartPageV31 = hydratableLazy('customV2_startPageV31', () => import('../steps/start/startPageV31'));
const CustomV2StartPageV32 = hydratableLazy('customV2_startPageV32', () => import('../steps/start/startPageV32'));
const CustomV2StartPageV33 = hydratableLazy('customV2_startPageV33', () => import('../steps/start/startPageV33'));
const CustomV2StartPageV41 = hydratableLazy('customV2_startPageV41', () => import('../steps/start/startPageV41'));
const CustomV2StartPageV34 = hydratableLazy('customV2_startPageV34', () => import('../steps/start/startPageV34'));
const CustomV2StartPageV35 = hydratableLazy('customV2_startPageV35', () => import('../steps/start/startPageV35'));
const CustomV2StartPageV42 = hydratableLazy('customV2_startPageV42', () => import('../steps/start/startPageV42'));
const CustomV2StartPageV43 = hydratableLazy('customV2_startPageV43', () => import('../steps/start/startPageV43'));
const CustomV2StartPageV37 = hydratableLazy('customV2_startPageV37', () => import('../steps/start/startPageV37'));
const CustomV2StartPageV38 = hydratableLazy('customV2_startPageV38', () => import('../steps/start/startPageV38'));
const CustomV2StartPageV36 = hydratableLazy('customV2_startPageV36', () => import('../steps/start/startPageV36'));
const CustomV2StartPageV39 = hydratableLazy('customV2_startPageV39', () => import('../steps/start/startPageV39'));
const CustomV2StartPageV40 = hydratableLazy('customV2_startPageV40', () => import('../steps/start/startPageV40'));
const CustomV2StartPageV44 = hydratableLazy('customV2_startPageV44', () => import('../steps/start/startPageV44'));
const CustomV2StartPageV45 = hydratableLazy('customV2_startPageV45', () => import('../steps/start/startPageV45'));
const CustomV2StartPageV46 = hydratableLazy('customV2_startPageV46', () => import('../steps/start/startPageV46'));
const CustomV2PolicyPageV2 = lazy(() => import('../steps/policy/policyV2'));
const CustomV2PolicyPageV4 = lazy(() => import('../steps/policy/policyV4'));
const CustomV2PolicyPageV5 = lazy(() => import('../steps/policy/policyV5'));
const CustomV2PolicyPageV6 = lazy(() => import('../steps/policy/policyV6'));

const WithOneOptionV2 = lazy(() => import('../steps/with/withOneOption'));
const WithOneOptionV3 = lazy(() => import('../steps/age/age'));
const WithOneOptionV5 = lazy(() => import('../steps/with/withOneOptionV3'));

const DogNameV2 = lazy(() => import('../steps/name'));
const DogBreed = lazy(() => import('../steps/breed'));
const CustomV2MultiOptionsV1 = lazy(() => import('../steps/multi/multiOptionsV1'));
const CustomV2MultiOptionsV2 = lazy(() => import('../steps/multi/multiOptionsV2'));
const CustomV2InfoBreed = lazy(() => import('../steps/info/infoBreed'));
const InfoImageText = lazy(() => import('../steps/infoImageText'));
const CustomV2InfoBreedV2 = lazy(() => import('../steps/info/infoBreedV2'));
const InfoPuppyEasier = lazy(() => import('../steps/info/infoPuppyEasier'));
const InfoPuppyBlues = lazy(() => import('../steps/info/infoPuppyBlues'));
const GettingPuppyMistake = lazy(() => import('../steps/gettingPuppyMistake'));
const NotEnjoyLifeWithDog = lazy(() => import('../steps/notEnjoyLifeWithDog'));
const CustomV2CreatingProgram = lazy(() => import('../steps/creatingProgram'));
const MailV3 = lazy(() => import('../steps/mail/mailV3'));
const Plan = lazy(() => import('../steps/plan'));
const InfoPredict = lazy(() => import('../steps/info/infoPredict'));
const DateStep = lazy(() => import('../steps/date'));
const WithTrackOption = lazy(() => import('../steps/with/withTrackOption'));
const ReceiveEmail = lazy(() => import('../steps/receiveEmail'));
const SpecialOfferV4 = lazy(() => import('../steps/special/specialOfferV4'));
const SpecialOfferV3 = lazy(() => import('../steps/special/specialOfferV3'));
const DiscountCards = lazy(() => import('../steps/discountCards'));
const StartPageAge = hydratableLazy('start_page_age', () => import('../steps/start/startPageAge'));

const WelcomeDiscount = lazy(() => import('../steps/welcomeDiscount'));
const WelcomeGift = lazy(() => import('../steps/welcomeGift'));
const StartPageAgeV3 = lazy(() => import('../steps/start/startPageAgeV3'));
const InfoAboutCrate = lazy(() => import('../steps/info/infoAboutCrate'));
const InfoAboutUnderstanding = lazy(() => import('../steps/info/infoAboutUnderstanding'));
const UnderstandingSummary = lazy(() => import('../steps/understandingSummary'));
const InfoAboutWorries = lazy(() => import('../steps/info/infoAboutWorries'));

// ─── Types ───────────────────────────────────────────────────────────────

export type StepData = {
  name: string;
  [key: string]: unknown;
};

type WrapperPropsConfig = {
  customIsCompletedValidation?: boolean;
};

type ChildPropsConfig = {
  withIcon?: boolean;
  noWideIcons?: boolean;
  withDescriptionText?: boolean;
  roundedOptions?: boolean;
  withWideButtons?: boolean;
  btn?: string;
  showBottomBar?: boolean;
};

type CmpProps = {
  wrapper?: WrapperPropsConfig;
  child?: ChildPropsConfig;
};

type CompletionLogicType = 'ifOneAnswerSelected' | 'ifAtLeastThreeAnswerSelected';

type CachedQuestion = {
  name: string;
  isCompleted: CompletionLogicType;
  [key: string]: unknown;
};

type QuestionExtras = Record<string, unknown> | null | undefined;

export type StepRenderer = (stepData: StepData) => React.ReactElement;

// =====================
//  УТИЛИТЫ ДЛЯ ПРОИЗВОДИТЕЛЬНОСТИ
// =====================

const withSuspense = (node: React.ReactElement): React.ReactElement => (
  <Suspense fallback={null}>{node}</Suspense>
);

const questionsCache = new Map<string, CachedQuestion[]>();
const makeKey = (base: string, extras: QuestionExtras): string => (
  JSON.stringify({ base, extras })
);

function cachedOne(name: string, extras?: QuestionExtras): CachedQuestion[] {
  const key = makeKey(name, extras ?? null);
  if (!questionsCache.has(key)) {
    questionsCache.set(key, [
      {
        name,
        isCompleted: 'ifOneAnswerSelected',
        ...(extras || {}),
      },
    ]);
  }
  return questionsCache.get(key)!;
}

const pickWrapperProps = (cfg?: WrapperPropsConfig): WrapperPropsConfig => {
  if (!cfg) return {};
  return {
    customIsCompletedValidation: cfg.customIsCompletedValidation,
  };
};

const pickChildProps = (cfg?: ChildPropsConfig): ChildPropsConfig => {
  if (!cfg) return {};
  return {
    withIcon: cfg.withIcon,
    noWideIcons: cfg.noWideIcons,
    withDescriptionText: cfg.withDescriptionText,
    roundedOptions: cfg.roundedOptions,
    withWideButtons: cfg.withWideButtons,
    btn: cfg.btn,
    showBottomBar: cfg.showBottomBar,
  };
};

// 3) Универсальные фабрики
const wrapStep = (Cmp: React.ElementType, cmpProps?: CmpProps): StepRenderer => (
  (stepData) => {
    const w = pickWrapperProps(cmpProps?.wrapper);
    const c = pickChildProps(cmpProps?.child);

    return withSuspense(
      <StepWrapper
        stepData={stepData}
        customIsCompletedValidation={w.customIsCompletedValidation}
      >
        <Cmp
          withIcon={c.withIcon}
          noWideIcons={c.noWideIcons}
          withDescriptionText={c.withDescriptionText}
          roundedOptions={c.roundedOptions}
          withWideButtons={c.withWideButtons}
          btn={c.btn}
          showBottomBar={c.showBottomBar}
        />
      </StepWrapper>,
    );
  }
);

const wrapStepQuestions = (
  Cmp: React.ElementType,
  qName: string,
  extras?: QuestionExtras,
  cmpProps?: CmpProps,
): StepRenderer => (stepData) => {
  const w = pickWrapperProps(cmpProps?.wrapper);
  const c = pickChildProps(cmpProps?.child);

  return withSuspense(
    <StepWrapper
      stepData={stepData}
      questions={cachedOne(qName, extras)}
      customIsCompletedValidation={w.customIsCompletedValidation}
    >
      <Cmp
        withIcon={c.withIcon}
        noWideIcons={c.noWideIcons}
        withDescriptionText={c.withDescriptionText}
        roundedOptions={c.roundedOptions}
        withWideButtons={c.withWideButtons}
        btn={c.btn}
        showBottomBar={c.showBottomBar}
      />
    </StepWrapper>,
  );
};

function pickStartPageCmp(): React.ElementType {
  return CustomV2StartPageV4;
}

// "Прокладка": получает пропсы от wrapStepQuestions и пробрасывает дальше
function DynamicCustomV2StartPage(props: Record<string, unknown>): React.ReactElement {
  const Cmp = pickStartPageCmp();
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <Cmp {...props} />;
}

// =====================
//  КОНФИГ КОМПОНЕНТОВ
// =====================

export const stepsComponents: Record<string, StepRenderer> = {
  mail: wrapStep(Mail),
  customV2_policyPageV2: wrapStepQuestions(CustomV2PolicyPageV2, 'customV2_policyPageV2'),
  customV2_policyPageV4: wrapStepQuestions(CustomV2PolicyPageV4, 'customV2_policyPageV4'),
  customV2_policyPageV5: wrapStepQuestions(CustomV2PolicyPageV5, 'customV2_policyPageV5'),
  customV2_policyPageV6: wrapStepQuestions(CustomV2PolicyPageV6, 'customV2_policyPageV6'),
  customV2_startPageV4: wrapStepQuestions(DynamicCustomV2StartPage, 'customV2_startPageV4'),
  customV2_startPageV29: wrapStepQuestions(CustomV2StartPageV29, 'customV2_startPageV29'),
  customV2_startPageV30: wrapStepQuestions(CustomV2StartPageV30, 'customV2_startPageV30'),
  customV2_startPageV31: wrapStepQuestions(CustomV2StartPageV31, 'customV2_startPageV31'),
  customV2_startPageV32: wrapStepQuestions(CustomV2StartPageV32, 'customV2_startPageV32'),
  customV2_startPageV33: wrapStepQuestions(CustomV2StartPageV33, 'customV2_startPageV33'),
  customV2_startPageV41: wrapStepQuestions(CustomV2StartPageV41, 'customV2_startPageV41'),
  customV2_startPageV34: wrapStepQuestions(CustomV2StartPageV34, 'customV2_startPageV34'),
  customV2_startPageV35: wrapStepQuestions(CustomV2StartPageV35, 'customV2_startPageV35'),
  customV2_startPageV37: wrapStepQuestions(CustomV2StartPageV37, 'customV2_startPageV37'),
  customV2_startPageV38: wrapStepQuestions(CustomV2StartPageV38, 'customV2_startPageV38'),
  customV2_startPageV36: wrapStepQuestions(CustomV2StartPageV36, 'customV2_startPageV36'),
  customV2_startPageV39: wrapStepQuestions(CustomV2StartPageV39, 'customV2_startPageV39'),
  customV2_startPageV40: wrapStepQuestions(CustomV2StartPageV40, 'customV2_startPageV40'),
  customV2_startPageV42: wrapStepQuestions(CustomV2StartPageV42, 'customV2_startPageV42'),
  customV2_startPageV43: wrapStepQuestions(CustomV2StartPageV43, 'customV2_startPageV43'),
  customV2_startPageV44: wrapStepQuestions(CustomV2StartPageV44, 'customV2_startPageV44'),
  customV2_startPageV45: wrapStepQuestions(CustomV2StartPageV45, 'customV2_startPageV45'),
  customV2_startPageV46: wrapStepQuestions(CustomV2StartPageV46, 'customV2_startPageV46'),

  customV2_gender: (stepData) => withSuspense(
    <StepWrapper stepData={stepData} questions={cachedOne('customV2_gender')}>
      <WithOneOptionV2 withIcon />
    </StepWrapper>,
  ),

  customV2_name: wrapStep(DogNameV2, { wrapper: { customIsCompletedValidation: true } }),
  customV2_drinking: wrapStepQuestions(WithOneOptionV2, 'customV2_drinking', null, { child: { withIcon: true } }),
  customV2_appetite: wrapStepQuestions(WithOneOptionV2, 'customV2_appetite', null, { child: { withIcon: true } }),
  customV2_stool_changes: wrapStepQuestions(WithOneOptionV2, 'customV2_stool_changes', null, { child: { withIcon: true } }),
  customV2_pain_signs: wrapStepQuestions(WithOneOptionV2, 'customV2_pain_signs', null, { child: { withIcon: true } }),
  customV2_recent_exposure: wrapStepQuestions(WithOneOptionV2, 'customV2_recent_exposure', null, { child: { withIcon: true } }),
  customV2_energy_level: wrapStepQuestions(WithOneOptionV2, 'customV2_energy_level', null, { child: { withIcon: true } }),
  customV2_scratching_licking: wrapStepQuestions(WithOneOptionV2, 'customV2_scratching_licking', null, { child: { withIcon: true } }),
  customV2_chewing_biting: wrapStepQuestions(WithOneOptionV2, 'customV2_chewing_biting', null, { child: { withIcon: true } }),
  customV2_sleep_changes: wrapStepQuestions(WithOneOptionV2, 'customV2_sleep_changes', null, { child: { withIcon: true } }),
  customV2_anxiety_restlessness: wrapStepQuestions(WithOneOptionV2, 'customV2_anxiety_restlessness', null, { child: { withIcon: true } }),
  customV2_vaccination_status: wrapStepQuestions(WithOneOptionV2, 'customV2_vaccination_status', null, { child: { withIcon: true } }),
  customV2_medication: wrapStepQuestions(WithOneOptionV2, 'customV2_medication', null, { child: { withIcon: true } }),
  customV2_supplements: wrapStepQuestions(WithOneOptionV2, 'customV2_supplements', null, { child: { withIcon: true } }),
  customV2_health_help_first: wrapStepQuestions(WithOneOptionV2, 'customV2_health_help_first', null, { child: { withIcon: true } }),
  customV2_health_worries: wrapStepQuestions(WithOneOptionV2, 'customV2_health_worries', null, { child: { withIcon: true } }),
  customV2_food_type: wrapStepQuestions(WithOneOptionV2, 'customV2_food_type', null, { child: { withIcon: true } }),
  customV2_food_allergies: wrapStepQuestions(WithOneOptionV2, 'customV2_food_allergies', null, { child: { withIcon: true } }),
  customV2_health_goals: wrapStepQuestions(CustomV2MultiOptionsV1, 'customV2_health_goals'),
  customV2_food_choice: wrapStepQuestions(CustomV2MultiOptionsV1, 'customV2_food_choice'),
  customV2_vet_access: wrapStepQuestions(WithOneOptionV2, 'customV2_vet_access', null, { child: { withIcon: true } }),
  customV2_late_night_worry: wrapStepQuestions(WithOneOptionV2, 'customV2_late_night_worry', null, { child: { withIcon: true } }),
  customV2_first_dog: wrapStepQuestions(WithOneOptionV2, 'customV2_first_dog', null, { child: { withIcon: true } }),
  customV2_age: wrapStepQuestions(WithOneOptionV3, 'customV2_age'),
  customV2_breed: wrapStep(DogBreed, { wrapper: { customIsCompletedValidation: true } }),
  customV2_puppy_feelings: wrapStepQuestions(WithOneOptionV2, 'customV2_puppy_feelings', null, { child: { withIcon: true } }),
  getting_puppy_mistake: wrapStepQuestions(GettingPuppyMistake, 'getting_puppy_mistake'),
  customV2_puppy_challenges: wrapStepQuestions(CustomV2MultiOptionsV1, 'customV2_puppy_challenges', {
    isResetAnswer: { index: 'v5', value: 'All of the above' },
  }),
  not_enjoy_life_with_dog: wrapStepQuestions(NotEnjoyLifeWithDog, 'not_enjoy_life_with_dog'),

  customV2_health: wrapStepQuestions(CustomV2MultiOptionsV1, 'customV2_health', {
    isResetAnswer: { index: 'v1', value: 'My dog is healthy' },
  }),
  customV2_potty: wrapStepQuestions(CustomV2MultiOptionsV1, 'customV2_potty', {
    isResetAnswer: { index: 'v4', value: 'All good, no issues!' },
  }),
  customV2_issues: wrapStepQuestions(CustomV2MultiOptionsV1, 'customV2_issues'),
  customV2_walking_issues: wrapStepQuestions(CustomV2MultiOptionsV1, 'customV2_walking_issues', {
    isResetAnswer: { index: 'v1', value: 'My dog doesnt walk out yet' },
  }),

  behavior_concern: wrapStepQuestions(CustomV2MultiOptionsV1, 'behavior_concern'),
  stranger_reaction: wrapStepQuestions(CustomV2MultiOptionsV1, 'stranger_reaction'),
  leash_behavior: wrapStepQuestions(CustomV2MultiOptionsV1, 'leash_behavior'),
  resource_guarding: wrapStepQuestions(WithOneOptionV2, 'resource_guarding', null, { child: { withIcon: true } }),
  aggression_history: wrapStepQuestions(CustomV2MultiOptionsV1, 'aggression_history', {
    isResetAnswer: { index: 'v1', value: 'No' },
  }),
  ignore_commands: wrapStepQuestions(WithOneOptionV2, 'ignore_commands', null, { child: { withIcon: true } }),
  k9_goal: wrapStepQuestions(CustomV2MultiOptionsV1, 'k9_goal'),

  customV2_activity: wrapStepQuestions(WithOneOptionV2, 'customV2_activity', null, { child: { withIcon: true } }),
  customV2_training_time: wrapStepQuestions(WithOneOptionV2, 'customV2_training_time'),
  customV2_training: wrapStepQuestions(WithOneOptionV2, 'customV2_training', null, { child: { withIcon: true } }),

  customV2_command_1: wrapStepQuestions(CustomV2MultiOptionsV2, 'customV2_command_1'),
  customV2_info_health: wrapStepQuestions(InfoImageText, 'customV2_info_health'),
  k9_info_personalization: wrapStepQuestions(InfoImageText, 'k9_info_personalization'),
  info_about_food_scanner: wrapStepQuestions(InfoImageText, 'info_about_food_scanner'),
  customV2_info_puppy_easier: wrapStepQuestions(InfoPuppyEasier, 'customV2_info_puppy_easier'),
  customV2_info_puppy_blues: wrapStepQuestions(InfoPuppyBlues, 'customV2_info_puppy_blues'),
  customV2_info_breed: wrapStepQuestions(CustomV2InfoBreed, 'customV2_info_breed'),
  customV2_info_breed_2: wrapStepQuestions(CustomV2InfoBreedV2, 'customV2_info_breed_2'),
  customV2_creating_program: wrapStepQuestions(CustomV2CreatingProgram, 'customV2_creating_program'),

  customV2_email: wrapStep(MailV3),
  customV2_plan: wrapStepQuestions(Plan, 'customV2_plan'),

  customV2_reason: wrapStepQuestions(WithOneOptionV5, 'customV2_reason', null, { child: { withIcon: true } }),
  customV2_date: wrapStep(DateStep, { wrapper: { customIsCompletedValidation: true } }),
  customV2_info_predict: wrapStepQuestions(InfoPredict, 'customV2_info_predict'),

  statement_irritation: wrapStepQuestions(WithTrackOption, 'statement_irritation'),
  statement_ignore_commands: wrapStepQuestions(WithTrackOption, 'statement_ignore_commands'),
  statement_strong_bond: wrapStepQuestions(WithTrackOption, 'statement_strong_bond'),
  statement_enjoy_time: wrapStepQuestions(WithTrackOption, 'statement_enjoy_time'),

  receive_email: wrapStep(ReceiveEmail),

  customV2_specialOffer_v4: wrapStep(SpecialOfferV4),
  customV2_specialOffer_v2: (stepData) => withSuspense(
    <StepWrapper stepData={stepData}>
      {isDiscountScreenVariantB() ? <WelcomeGift /> : <DiscountCards />}
    </StepWrapper>,
  ),
  customV2_specialOffer_v3: wrapStep(SpecialOfferV3),
  customV2_welcome_discount: wrapStep(WelcomeDiscount),
  start_page_age: wrapStepQuestions(StartPageAge, 'start_page_age'),
  start_page_age_v3: wrapStepQuestions(StartPageAgeV3, 'start_page_age_v3'),
  crate_trained: wrapStepQuestions(WithOneOptionV2, 'crate_trained', null, { child: { withIcon: true } }),
  info_about_crate: wrapStepQuestions(InfoAboutCrate, 'info_about_crate'),
  age_group: wrapStepQuestions(WithOneOptionV2, 'age_group', null, { child: { withIcon: false } }),
  understanding: wrapStepQuestions(WithOneOptionV2, 'understanding', null, { child: { withIcon: true } }),
  info_about_understanding: wrapStepQuestions(InfoAboutUnderstanding, 'info_about_understanding'),
  understanding_when: wrapStepQuestions(CustomV2MultiOptionsV1, 'understanding_when'),
  understanding_real_time: wrapStepQuestions(CustomV2MultiOptionsV1, 'understanding_real_time', {
    isResetAnswer: { index: 'v4', value: 'Not really' },
  }),
  customV2_play_frequency: wrapStepQuestions(WithOneOptionV2, 'customV2_play_frequency'),
  customV2_play_games: wrapStepQuestions(CustomV2MultiOptionsV1, 'customV2_play_games', {
    isResetAnswer: { index: 'v5', value: 'Not sure yet' },
  }),
  customV2_play_behavior: wrapStepQuestions(WithOneOptionV2, 'customV2_play_behavior'),
  customV2_play_ideas: wrapStepQuestions(WithOneOptionV2, 'customV2_play_ideas'),
  understanding_summary: wrapStepQuestions(UnderstandingSummary, 'understanding_summary'),
  behavior_signals: wrapStepQuestions(InfoAboutCrate, 'behavior_signals'),
  health_uncertainty: wrapStepQuestions(WithTrackOption, 'health_uncertainty'),
  info_about_worries: wrapStepQuestions(InfoAboutWorries, 'info_about_worries'),
  info_about_nutrition: wrapStepQuestions(InfoAboutWorries, 'info_about_nutrition'),
};
